import React, { useState, useEffect } from 'react';
import { FiMenu, FiX, FiSun, FiMoon } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import LanguageSwitch from '../LanguageSwitch/LanguageSwitch';

const Navbar = () => {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const [darkMode, setDarkMode] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const [activeSection, setActiveSection] = useState('home');
    const [scrollProgress, setScrollProgress] = useState(0);

    // Dark mode: localStorage → fallback to system preference
    useEffect(() => {
        const saved = localStorage.getItem('darkMode');
        const isDark = saved !== null
            ? JSON.parse(saved)
            : window.matchMedia('(prefers-color-scheme: dark)').matches;
        setDarkMode(isDark);
        if (isDark) document.documentElement.classList.add('dark');
    }, []);

    // Scroll: background + progress bar
    useEffect(() => {
        const handleScroll = () => {
            setScrolled(window.scrollY > 20);
            const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
            setScrollProgress(maxScroll > 0 ? (window.scrollY / maxScroll) * 100 : 0);
        };
        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // Dark mode: persist + apply
    useEffect(() => {
        localStorage.setItem('darkMode', JSON.stringify(darkMode));
        document.documentElement.classList.toggle('dark', darkMode);
    }, [darkMode]);

    // Active section via IntersectionObserver
    useEffect(() => {
        const ids = ['home', 'about', 'experience', 'projects', 'homelab', 'bots', 'ai', 'contact'];
        const observers = ids.map(id => {
            const el = document.getElementById(id);
            if (!el) return null;
            const obs = new IntersectionObserver(
                ([entry]) => { if (entry.isIntersecting) setActiveSection(id); },
                { rootMargin: '-40% 0px -55% 0px' }
            );
            obs.observe(el);
            return obs;
        });
        return () => observers.forEach(obs => obs?.disconnect());
    }, []);

    const navLinks = [
        { key: 'home', href: '#home', section: 'home' },
        { key: 'about', href: '#about', section: 'about' },
        { key: 'experience', href: '#experience', section: 'experience' },
        { key: 'projects', href: '#projects', section: 'projects' },
        { key: 'lab', href: '#homelab', section: 'homelab' },
        { key: 'bots', href: '#bots', section: 'bots' },
        { key: 'ai', href: '#ai', section: 'ai' },
        { key: 'contact', href: '#contact', section: 'contact' },
    ];

    return (
        <nav className={`fixed w-full z-50 transition-all duration-300 ${
            scrolled || isOpen
                ? 'bg-white/70 dark:bg-zinc-950/70 backdrop-blur-xl border-b border-white/40 dark:border-zinc-800/50 shadow-sm'
                : 'bg-transparent'
        }`}>
            {/* Scroll progress bar */}
            <div
                className="absolute top-0 left-0 h-0.5 bg-amber-500 dark:bg-amber-400 transition-all duration-75 pointer-events-none"
                style={{ width: `${scrollProgress}%` }}
            />

            <div className="container-custom px-5 sm:px-6 lg:px-8">
                <div className="flex justify-between items-center h-16 min-w-0">
                    {/* Logo */}
                    <a href="#home" className="shrink-0 font-mono text-lg font-medium text-amber-600 dark:text-amber-400 tracking-tight">
                        rontaro<span className="text-zinc-400 dark:text-zinc-500">.</span>
                    </a>

                    {/* Desktop Menu */}
                    <div className="hidden md:flex items-center gap-6">
                        {navLinks.map(({ key, href, section }) => (
                            <a
                                key={key}
                                href={href}
                                className={`font-mono text-xs tracking-widest uppercase transition-colors ${
                                    activeSection === section
                                        ? 'text-amber-600 dark:text-amber-400'
                                        : 'text-zinc-500 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100'
                                }`}
                            >
                                {t(`navbar.${key}`)}
                            </a>
                        ))}
                        <LanguageSwitch />
                        <button
                            onClick={() => setDarkMode(d => !d)}
                            className="p-2 text-zinc-500 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-zinc-100 transition-colors"
                            aria-label={t('navbar.toggleDarkMode')}
                        >
                            {darkMode ? <FiSun size={18} /> : <FiMoon size={18} />}
                        </button>
                    </div>

                    {/* Mobile buttons */}
                    <div className="md:hidden flex items-center gap-2">
                        <LanguageSwitch />
                        <button
                            onClick={() => setDarkMode(d => !d)}
                            className="p-2 text-zinc-500 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-zinc-100 transition-colors"
                            aria-label={t('navbar.toggleDarkMode')}
                        >
                            {darkMode ? <FiSun size={18} /> : <FiMoon size={18} />}
                        </button>
                        <button
                            onClick={() => setIsOpen(o => !o)}
                            className="text-zinc-700 dark:text-zinc-300"
                            aria-label={t('navbar.toggleMenu')}
                        >
                            {isOpen ? <FiX size={24} /> : <FiMenu size={24} />}
                        </button>
                    </div>
                </div>

                {/* Mobile Menu */}
                {isOpen && (
                    <div className="md:hidden pb-4 border-t border-zinc-200 dark:border-zinc-800 pt-4">
                        {navLinks.map(({ key, href, section }) => (
                            <a
                                key={key}
                                href={href}
                                className={`block py-2.5 font-mono text-xs tracking-widest uppercase transition-colors ${
                                    activeSection === section
                                        ? 'text-amber-600 dark:text-amber-400'
                                        : 'text-zinc-500 dark:text-zinc-400'
                                }`}
                                onClick={() => setIsOpen(false)}
                            >
                                {t(`navbar.${key}`)}
                            </a>
                        ))}
                    </div>
                )}
            </div>
        </nav>
    );
};

export default Navbar;
